import React, { useState } from "react";
import { List, Map } from "immutable";
import { Button, Tooltip, Text } from "@showitinc/showit-wc";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import UIActions from "../../../actions/UIActions";
import AppConstants from "../../../constants/AppConstants";
import LayerPanelBlock from "./LayerPanelBlock";
import BlockUpdateVO from "../../../vo/BlockUpdate";
import CanvasSetLayers from "./CanvasSetLayers";
import PropertiesPanelCanvasSetSection from "../../PropertiesPanelCanvasSetSection";
import LayerPanelCanvasSetBlock from "./LayerPanelCanvasSetBlock";
import DesignViewStore from "../../../stores/DesignViewStore";
import PanelHeader from "../../../app2components/PanelHeader/PanelHeader";
import PagePanelHeaderContextMenu from "../../../app2components/ContextMenus/PagePanelHeaderContextMenu";
import styles from "./LayersPanel.module.css";

const {
  CANVAS_SET_LAYER_PANEL_PAGE,
  ITEM_TYPE_CANVAS_SET,
  ITEM_TYPE_BLOCK,
  SITE_ITEM_PAGE,
  STAGE_VIEW_MOBILE,
  STAGE_VIEW_SPLIT,
  VISIBLE_ALL,
  VISIBLE_DESKTOP,
  VISIBLE_NONE,
  VISIBLE_MOBILE,
} = AppConstants;

const currentCanvasSetPath = ["currentPageItem", "canvasSet"];

const getCanvasSetName = (canvasSets, blockContainer) => {
  const narrowedList =
    canvasSets
      .filter((canvasSet) => {
        const id = blockContainer.getIn(currentCanvasSetPath, List()).first();
        return canvasSet.get("id") === id;
      })
      .first() || Map();
  return narrowedList.get("name", "");
};

const LayersPanel = ({ blockContainer, canvasSets = List(), pages, siteBlocks, stageView }) => {
  const hasCanvasSet =
    !blockContainer.getIn(currentCanvasSetPath, List()).isEmpty() && blockContainer.get("isPage");

  const canvasSetName = hasCanvasSet && getCanvasSetName(canvasSets, blockContainer);

  const [canvasSetVisibility, setCanvasSetVisibility] = useState({ top: false, bottom: false });

  const toggleCanvasSetVisibility = (area) => () => {
    setCanvasSetVisibility((state) => ({ ...state, [area]: !state[area] }));
  };

  const _selectBlock = (block) => {
    if (block.get("selected") == null) {
      const selectedBlock = blockContainer.get("selectedBlock");
      const layout = !selectedBlock.isEmpty()
        ? selectedBlock.getIn(["selected", "layout"])
        : stageView === STAGE_VIEW_SPLIT
        ? STAGE_VIEW_MOBILE
        : stageView;
      UIActions.selectBlock({ id: block.get("id"), layout });
    }
    UIActions.scrollIntoView({ id: block.get("id"), type: "block" });
  };

  const _updateVisible = (id, selectedVis, currentVis) => {
    const visibilities = {
      [VISIBLE_MOBILE]: {
        [VISIBLE_ALL]: VISIBLE_DESKTOP,
        [VISIBLE_DESKTOP]: VISIBLE_ALL,
        [VISIBLE_MOBILE]: VISIBLE_NONE,
        [VISIBLE_NONE]: VISIBLE_MOBILE,
      },
      [VISIBLE_DESKTOP]: {
        [VISIBLE_ALL]: VISIBLE_MOBILE,
        [VISIBLE_DESKTOP]: VISIBLE_NONE,
        [VISIBLE_MOBILE]: VISIBLE_ALL,
        [VISIBLE_NONE]: VISIBLE_DESKTOP,
      },
    };

    const newVis = (visibilities[selectedVis] || {})[currentVis] || currentVis;

    const update = new BlockUpdateVO();
    update.add(id, "visible", newVis);

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  const _onBackToPage = (page) => {
    UIActions.selectPage({ page });
    UIActions.removeLastActiveItemHistory();
  };

  const _onBackToCanvasSet = (canvasSet) => {
    UIActions.selectCanvasSet({ canvasSet });
    UIActions.removeLastActiveItemHistory();
  };

  const _onLayerMove = (data) => {
    DocumentStore.dispatch({ type: COMMANDS.MOVE_LAYER, payload: data });
  };

  const _clearSelection = () => {
    const layout = stageView === STAGE_VIEW_SPLIT ? STAGE_VIEW_MOBILE : stageView;
    UIActions.clearSelectedPage(layout);
  };

  const _getBackToPageButton = (container) => {
    const lastActiveItems = DesignViewStore.getActiveItemHistory()?.toJS();
    const selectedBlock = container.get("selectedBlock");
    const isSelectSiteBlock = selectedBlock.get("id") === container.get("id");
    const lastItemCount = lastActiveItems?.length;
    const [lastType, lastId] = (lastItemCount > 1 && lastActiveItems[lastItemCount - 2]) || [];

    const currentCanvasSet = container.getIn(["canvasSets", lastId]);
    const currentPageItem = container.get("currentPageItem");
    const tooltipText = `Back to ${
      lastType === ITEM_TYPE_CANVAS_SET
        ? `"${currentCanvasSet.get("name")}" Canvas Set`
        : `"${currentPageItem.get("name")}" Page`
    }`;

    const button =
      lastType === ITEM_TYPE_CANVAS_SET ? (
        <Button
          variant="icon"
          icon-prefix="arrowChevronLeft"
          iconSize="20"
          onClick={() => _onBackToCanvasSet(currentCanvasSet)}
        />
      ) : lastType === SITE_ITEM_PAGE || isSelectSiteBlock ? (
        <Button
          variant="icon"
          icon-prefix="arrowChevronLeft"
          iconSize="20"
          onClick={() => _onBackToPage(currentPageItem)}
        />
      ) : null;

    return button ? (
      <Tooltip position="bottom-right" delay="0">
        {button}
        <Text slot="content">{tooltipText}</Text>
      </Tooltip>
    ) : null;
  };

  const _mapBlocks = (blocks) =>
    blocks.map((block, index) => (
      <LayerPanelBlock
        key={block.get("id")}
        id={block.get("id")}
        canvasSet={block.get("canvasSet")}
        index={index}
        layerType={ITEM_TYPE_BLOCK}
        block={block}
        stageView={stageView}
        onSelect={() => _selectBlock(block)}
        onLayerMove={_onLayerMove}
        onVisibleChange={_updateVisible}
      />
    ));

  const _mapCanvasSetBlocks = (blocks) =>
    blocks.map((block, index) => (
      <LayerPanelCanvasSetBlock
        key={block.get("id")}
        id={block.get("id")}
        canvasSet={block.get("canvasSet")}
        index={index}
        block={block}
        onSelect={() => _selectBlock(block)}
        onLayerMove={_onLayerMove}
        onVisibleChange={_updateVisible}
        draggable={false}
        panel={CANVAS_SET_LAYER_PANEL_PAGE}
      />
    ));

  const [topBlocks, blocks, bottomBlocks] = blockContainer.get("blocks", List()).reduce(
    (result, block) => {
      const TOP_INDEX = 0;
      const CONTENT_INDEX = 1;
      const BOTTOM_INDEX = 2;
      const index = block.get("canvasSet")
        ? block.get("canvasSetPosition") === "top"
          ? TOP_INDEX
          : BOTTOM_INDEX
        : CONTENT_INDEX;

      result[index].push(block);
      return result;
    },
    [[], [], []]
  );
  const label = blockContainer.get("name");
  let headerSelected = blockContainer.get("selectedBlock").isEmpty();
  const isPage = blockContainer.get("isPage");
  const isBlock = blockContainer.get("isBlock");
  const isCanvasSet = blockContainer.get("isCanvasSet");

  let layerPanelSelect = null;
  let backToPageButton = null;
  if (isPage) {
    layerPanelSelect = _clearSelection;
  } else if (isBlock || isCanvasSet) {
    headerSelected = true; // have header selected
    backToPageButton = _getBackToPageButton(blockContainer);
  }

  return (
    <div className={styles["panel-layers"]} data-intercom-target="layer panel">
      <PanelHeader
        label={label}
        selected={headerSelected}
        onSelect={layerPanelSelect}
        backToPageButton={backToPageButton}
        panelType={SITE_ITEM_PAGE}
      >
        <PagePanelHeaderContextMenu
          pages={pages}
          siteBlocks={siteBlocks}
          canvasSets={canvasSets}
          blockContainer={blockContainer}
          active={headerSelected}
        />
      </PanelHeader>
      <div className={styles.panel}>
        {isCanvasSet ? (
          <CanvasSetLayers
            id={blockContainer.get("id")}
            blocks={[...topBlocks, ...bottomBlocks]}
            siteBlocks={siteBlocks}
            onSelect={_selectBlock}
            canvasSets={canvasSets}
            onLayerMove={_onLayerMove}
          />
        ) : (
          <>
            {hasCanvasSet && (
              <PropertiesPanelCanvasSetSection
                caret
                icon={null}
                label={canvasSetName}
                toggleVisibility={toggleCanvasSetVisibility("top")}
                visible={canvasSetVisibility.top}
                blocks={topBlocks}
                blockContainer={blockContainer}
              >
                {_mapCanvasSetBlocks(topBlocks)}
              </PropertiesPanelCanvasSetSection>
            )}
            <div>{_mapBlocks(blocks)}</div>
            {hasCanvasSet && (
              <PropertiesPanelCanvasSetSection
                caret
                icon={null}
                label={canvasSetName}
                toggleVisibility={toggleCanvasSetVisibility("bottom")}
                visible={canvasSetVisibility.bottom}
                blocks={bottomBlocks}
                blockContainer={blockContainer}
              >
                {_mapCanvasSetBlocks(bottomBlocks)}
              </PropertiesPanelCanvasSetSection>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LayersPanel;
